import { FAIconName, TagColor, TagProps } from '@equip.health/ui';
import { CarePlanGoalStatus } from '~/lib/types/carePlan/CarePlanGoalStatus';
import { OrderedEnumeration } from '~/lib/types/carePlan/carePlans';

// The goal-status reference endpoints return only code/label/sortOrder, so tag
// colors are mapped here by status code.
const GoalStatusTagColors: Record<string, TagColor> = {
  [CarePlanGoalStatus.NOT_STARTED]: TagColor.purple,
  [CarePlanGoalStatus.NOT_PROGRESSING]: TagColor.orange,
  [CarePlanGoalStatus.PROGRESSING]: TagColor.green,
  [CarePlanGoalStatus.COMPLETE]: TagColor.periwinkle,
  [CarePlanGoalStatus.NOT_ASSESSED]: TagColor.gray,
  [CarePlanGoalStatus.REPORTED]: TagColor.purple,
  [CarePlanGoalStatus.IMPROVING]: TagColor.green,
  [CarePlanGoalStatus.WORSENING]: TagColor.orange,
  [CarePlanGoalStatus.STABLE]: TagColor.marigold,
  [CarePlanGoalStatus.RESOLVED]: TagColor.periwinkle,
};

export const getTagColorByCarePlanGoalStatusCode = (statusCode: string) => {
  return GoalStatusTagColors[statusCode] ?? TagColor.gray;
};

// TagDropdown renders its selected button by looking up the current `value` in `menuOptions`.  For those "displayed but not selectable"
// states, hand TagDropdown a `placeholderTag` and leave `value` undefined so the selected button still renders with the right color.
export const buildGoalStatusTagDropdownProps = (
  selectedStatusCode: string,
  selectedStatusLabel: string,
  menuOptions: TagProps[],
): { placeholderTag?: TagProps; value?: string } => {
  const isPickable = menuOptions.some((o) => o.value === selectedStatusLabel);
  if (isPickable) {
    return { value: selectedStatusLabel };
  }
  return {
    placeholderTag: {
      color: getTagColorByCarePlanGoalStatusCode(selectedStatusCode),
      id: selectedStatusCode,
      value: selectedStatusLabel,
    },
  };
};

const NotStartedOption: TagProps = {
  color: getTagColorByCarePlanGoalStatusCode(CarePlanGoalStatus.NOT_STARTED),
  value: 'Not started',
  id: CarePlanGoalStatus.NOT_STARTED,
};

const EdCarePlanGoalCoreOptions: TagProps[] = [
  {
    color: getTagColorByCarePlanGoalStatusCode(
      CarePlanGoalStatus.NOT_PROGRESSING,
    ),
    value: 'Not progressing',
    id: CarePlanGoalStatus.NOT_PROGRESSING,
  },
  {
    color: getTagColorByCarePlanGoalStatusCode(CarePlanGoalStatus.PROGRESSING),
    value: 'Progressing',
    id: CarePlanGoalStatus.PROGRESSING,
  },
  {
    color: getTagColorByCarePlanGoalStatusCode(CarePlanGoalStatus.COMPLETE),
    value: 'Complete',
    id: CarePlanGoalStatus.COMPLETE,
  },
];

export const getCarePlanGoalTagDropdownOptions = (
  isMedPsychProblemsEnabled: boolean,
): TagProps[] =>
  isMedPsychProblemsEnabled
    ? EdCarePlanGoalCoreOptions
    : [NotStartedOption, ...EdCarePlanGoalCoreOptions];

const NotAssessedOption: TagProps = {
  color: getTagColorByCarePlanGoalStatusCode(CarePlanGoalStatus.NOT_ASSESSED),
  id: CarePlanGoalStatus.NOT_ASSESSED,
  value: 'Not assessed',
  leftIcon: FAIconName.eyeSlashRegular,
};

// Valid goal statuses that are never offered inline on the care plan: Void is
// reachable only via the kebab, and Not assessed is a note-only selection.
const StatusesAlwaysExcludedFromCarePlan = new Set<string>([
  CarePlanGoalStatus.VOID,
  CarePlanGoalStatus.NOT_ASSESSED,
]);

const EntryStateStatusesExcludedWhenMedPsychEnabled = new Set<string>([
  CarePlanGoalStatus.NOT_STARTED,
  CarePlanGoalStatus.REPORTED,
]);

/**
 * Maps goal statuses from the problem-types-goal-statuses map into tag dropdown
 * options for the care plan, preserving the order the API returns them in (the
 * endpoint already sorts each problem type's statuses by sortOrder).
 */
export const toCarePlanGoalTagDropdownOptions = (
  goalStatuses: OrderedEnumeration[] = [],
  isMedPsychProblemsEnabled: boolean = false,
): TagProps[] =>
  goalStatuses
    .filter(({ code }) => {
      if (StatusesAlwaysExcludedFromCarePlan.has(code)) return false;
      return !(
        isMedPsychProblemsEnabled &&
        EntryStateStatusesExcludedWhenMedPsychEnabled.has(code)
      );
    })
    .map(({ code, label }) => ({
      color: getTagColorByCarePlanGoalStatusCode(code),
      id: code,
      value: label,
    }));

export const getCarePlanNoteGoalTagDropdownOptions = (
  isMedPsychProblemsEnabled: boolean,
): TagProps[] => [
  ...getCarePlanGoalTagDropdownOptions(isMedPsychProblemsEnabled),
  NotAssessedOption,
];

/**
 * Note-only variant: the same options as the care plan, plus Not assessed when
 * the problem type's mapping includes it.
 *
 * Not assessed is pinned last rather than placed by sortOrder. sortOrder is a
 * single global column on ref_care_plan_goal_status (Not assessed is 6, the
 * med/psych statuses are 7-11), so the endpoint returns it first for med/psych
 * and last for ED. Where it renders is a presentation rule, not reference data.
 */
export const toCarePlanNoteGoalTagDropdownOptions = (
  goalStatuses: OrderedEnumeration[] = [],
  isMedPsychProblemsEnabled: boolean = false,
): TagProps[] => {
  const baseOptions = toCarePlanGoalTagDropdownOptions(
    goalStatuses,
    isMedPsychProblemsEnabled,
  );
  const notAssessed = goalStatuses.find(
    ({ code }) => code === CarePlanGoalStatus.NOT_ASSESSED,
  );

  return notAssessed
    ? [...baseOptions, { ...NotAssessedOption, value: notAssessed.label }]
    : baseOptions;
};
